import { useAuth0 } from '@auth0/auth0-react';
import { Toaster } from '@sunstone/ui';
import { Navigate, Outlet, Route, Routes } from 'react-router';
import { AccessBlockedPage } from '@/components/ui/AccessBlockedPage';
import { LoadingScreen } from '@/components/ui/LoadingScreen';
import { AuthGuard, TenantGuard } from '@/features/auth';
import { useAuth0Sync } from '@/features/auth/useAuth0Sync';
import { useAuthorization } from '@/features/auth/useAuthorization';
import { useTenantTypeSync } from '@/features/auth/useTenantTypeSync';
import { CallbackPage } from '@/pages/CallbackPage';
import { ChatPage } from '@/pages/ChatPage';
import { CustomersPage } from '@/pages/CustomersPage';
import { DashboardPage } from '@/pages/DashboardPage';
import { DeploymentsPage } from '@/pages/DeploymentsPage';
import { EditTenantPage } from '@/pages/EditTenantPage';
import { HelpPage } from '@/pages/HelpPage';
import { HomePage } from '@/pages/HomePage';
import { ImportOnPremClusterPage } from '@/pages/ImportOnPremClusterPage';
import { InfrastructurePage } from '@/pages/InfrastructurePage';
import { NewCustomerPage } from '@/pages/NewCustomerPage';
import { NewDeploymentPage } from '@/pages/NewDeploymentPage';
import { NewPartnerPage } from '@/pages/NewPartnerPage';
import { NewWorkspacePage } from '@/pages/NewWorkspacePage';
import { PartnersPage } from '@/pages/PartnersPage';
import { PlatformUsersPage } from '@/pages/PlatformUsersPage';
import { PreferencesPage } from '@/pages/PreferencesPage';
import { ReleasesPage } from '@/pages/ReleasesPage';
import { RoleAccessWizard } from '@/pages/RoleAccessWizard';
import { RoleDetailPage } from '@/pages/RoleDetailPage';
import { SettingsPage } from '@/pages/SettingsPage';
import { SignedOutPage } from '@/pages/SignedOutPage';
import { StatusPage } from '@/pages/StatusPage';
import { SystemRolesPage } from '@/pages/SystemRolesPage';
import { UserAccessWizard } from '@/pages/UserAccessWizard';
import { UserDetailPage } from '@/pages/UserDetailPage';
import { WorkspacePage } from '@/pages/WorkspacePage';
import { WorkspacesPage } from '@/pages/WorkspacesPage';
import { useAppSelector } from '@/store';
import { AppShell } from '../components/layout/AppShell';
import { useIsServer } from './ServerContext';

export function App() {
  const isServer = useIsServer();
  let isLoading = false;

  // Only use Auth0 on client side
  if (!isServer) {
    // Sync Auth0 state with Redux store
    // biome-ignore lint/correctness/useHookAtTopLevel: Intentional SSR guard — rendered conditionally only on client
    const { isSyncing } = useAuth0Sync();
    // biome-ignore lint/correctness/useHookAtTopLevel: Intentional SSR guard — rendered conditionally only on client
    useTenantTypeSync();
    // biome-ignore lint/correctness/useHookAtTopLevel: Intentional SSR guard — rendered conditionally only on client
    const authz = useAuthorization();
    const { userTenantType, isPlatformAdmin, scope, isIdentityLoading, isAccountDisabled } = authz;
    // biome-ignore lint/correctness/useHookAtTopLevel: Intentional SSR guard — rendered conditionally only on client
    const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
    // biome-ignore lint/correctness/useHookAtTopLevel: Intentional SSR guard — rendered conditionally only on client
    const sessionExpired = useAppSelector((state) => state.auth.sessionExpired);

    try {
      // biome-ignore lint/correctness/useHookAtTopLevel: Intentional SSR guard — rendered conditionally only on client
      const auth = useAuth0();
      isLoading = auth.isLoading || isSyncing;
    } catch (_error) {
      // Auth0 not available (non-HTTPS context)
      isLoading = false;
    }

    // Show loading screen while Auth0 initializes
    if (isLoading) {
      return <LoadingScreen />;
    }

    // Wait while tenant type is resolving (non-platform-admin only), or
    // while the current user's identity is still loading — otherwise every
    // hard refresh briefly evaluates hasValidAccess with isSunstone* still
    // false-by-default and flashes the "No Access" page below before the
    // real identity arrives.
    if (isAuthenticated && isIdentityLoading) {
      return <LoadingScreen />;
    }
    if (isAuthenticated && !isPlatformAdmin && userTenantType === 'pending') {
      return <LoadingScreen />;
    }

    // A stale/expired token that couldn't be silently refreshed also
    // poisons scope to null (GetCurrentUser fails) — check this first so
    // affected users see "log in again" instead of "contact your
    // administrator".
    if (isAuthenticated && sessionExpired) {
      return (
        <AccessBlockedPage
          title="Session Expired"
          message="Your session has expired. Please log in again."
          action={{ type: 'login' }}
        />
      );
    }

    // A disabled/deleted account (PLAT-124) also poisons scope to null
    // (GetCurrentUser fails) — check this first too, so a disabled user
    // sees "account disabled" rather than the generic "no access" page.
    if (isAuthenticated && isAccountDisabled) {
      return (
        <AccessBlockedPage
          title="Account Disabled"
          message="Your account has been disabled."
          detail="Contact an administrator to restore access."
          action={{ type: 'logout' }}
        />
      );
    }

    // Block: must have a resolved scope (all/partner/tenant) — scope ===
    // null means unprovisioned or still loading.
    const hasValidAccess = scope !== null;
    if (isAuthenticated && !hasValidAccess) {
      return (
        <AccessBlockedPage
          title="No Access"
          message="Your account doesn't have access to Sunstone. Contact your administrator."
          action={{ type: 'logout' }}
        />
      );
    }
  }

  return (
    <>
      <Toaster />
      <Routes>
        {/* Public routes with layout */}
        <Route element={<ShellLayout />}>
          <Route index element={<HomePage />} />
          <Route path="/" element={<HomePage />} />
          <Route path="/status" element={<StatusPage />} />
          <Route path="/signed-out" element={<SignedOutPage />} />
        </Route>

        {/* Auth callback (public, no layout) - client only */}
        {!isServer && <Route path="/callback" element={<CallbackPage />} />}

        {/* Protected: all authenticated users (sunstone:user and above), non-tenant-scoped */}
        <Route
          element={
            isServer ? (
              <ShellLayout />
            ) : (
              <AuthGuard>
                <ShellLayout />
              </AuthGuard>
            )
          }
        >
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/deployments" element={<DeploymentsPage />} />
          <Route path="/workspaces" element={<WorkspacesPage />} />
          <Route path="/workspaces/new" element={<NewWorkspacePage />} />
          <Route path="/deployments/new" element={<NewDeploymentPage />} />
          <Route path="/deployments/import" element={<ImportOnPremClusterPage />} />
          <Route path="/chat" element={<ChatPage />} />
          <Route path="/preferences" element={<PreferencesPage />} />
          <Route path="/help" element={<HelpPage />} />
        </Route>

        {/* Protected: any authenticated user — viewing is never gated by a
            permission (ReleasesPage is visible to every authenticated role;
            Sync/Deprecate buttons are their own hasPermission checks). */}
        <Route
          element={
            isServer ? (
              <ShellLayout />
            ) : (
              <AuthGuard>
                <ShellLayout />
              </AuthGuard>
            )
          }
        >
          <Route path="/releases" element={<ReleasesPage />} />
        </Route>

        {/* Protected + tenant-ownership check: any authenticated user visiting a :tenantId route */}
        <Route
          element={
            isServer ? (
              <ShellLayout />
            ) : (
              <AuthGuard>
                <TenantGuard>
                  <ShellLayout />
                </TenantGuard>
              </AuthGuard>
            )
          }
        >
          <Route path="/tenants/:tenantId" element={<CustomersPage />} />
          <Route path="/tenants/:tenantId/deployments/new" element={<NewDeploymentPage />} />
          <Route
            path="/tenants/:tenantId/deployments/:deploymentId"
            element={<DeploymentsPage />}
          />
          <Route
            path="/tenants/:tenantId/deployments/:deploymentId/workspaces/new"
            element={<NewWorkspacePage />}
          />
          <Route
            path="/tenants/:tenantId/deployments/:deploymentId/workspaces/:workspaceId"
            element={<WorkspacePage />}
          />
        </Route>

        {/* Protected + tenant-ownership check: manage-access (Admin/Operator Admin) visiting /tenants/:tenantId/edit */}
        <Route
          element={
            isServer ? (
              <ShellLayout />
            ) : (
              <AuthGuard requiredManageAccess>
                <TenantGuard>
                  <ShellLayout />
                </TenantGuard>
              </AuthGuard>
            )
          }
        >
          <Route path="/tenants/:tenantId/edit" element={<EditTenantPage />} />
          <Route path="/tenants/:tenantId/invite" element={<UserAccessWizard />} />
          <Route path="/tenants/:tenantId/users/:userId" element={<UserDetailPage />} />
          <Route path="/tenants/:tenantId/users/:userId/edit" element={<UserAccessWizard />} />
        </Route>

        {/* Protected: manage-access, non-tenant-scoped (role detail is
            itself tenant-agnostic; the backend enforces tenant access on
            GetRole for a custom role) */}
        <Route
          element={
            isServer ? (
              <ShellLayout />
            ) : (
              <AuthGuard requiredManageAccess>
                <ShellLayout />
              </AuthGuard>
            )
          }
        >
          <Route path="/roles/:roleId" element={<RoleDetailPage />} />
          <Route path="/roles/new" element={<RoleAccessWizard />} />
          <Route path="/roles/:roleId/edit" element={<RoleAccessWizard />} />
        </Route>

        {/* Protected: manage-access (sunstone:admin, sunstone:operator_admin), non-tenant-scoped */}
        <Route
          element={
            isServer ? (
              <ShellLayout />
            ) : (
              <AuthGuard requiredManageAccess>
                <ShellLayout />
              </AuthGuard>
            )
          }
        >
          <Route path="/settings" element={<SettingsPage />} />
        </Route>

        {/* Protected: partner-level users (sunstone:user at a partner tenant, or operator_admin) */}
        <Route
          element={
            isServer ? (
              <ShellLayout />
            ) : (
              <AuthGuard requiredTenantType="partner">
                <ShellLayout />
              </AuthGuard>
            )
          }
        >
          <Route path="/customers/new" element={<NewCustomerPage />} />
        </Route>

        {/* Protected: platform-scope identity, any role (Partners, full Customers list) */}
        <Route
          element={
            isServer ? (
              <ShellLayout />
            ) : (
              <AuthGuard requiredScope="all">
                <ShellLayout />
              </AuthGuard>
            )
          }
        >
          <Route path="/customers" element={<CustomersPage />} />
          <Route path="/partners" element={<PartnersPage />} />
          <Route path="/partners/new" element={<NewPartnerPage />} />
        </Route>

        {/* Protected: platform-wide surfaces (tenant-less Admin only) */}
        <Route
          element={
            isServer ? (
              <ShellLayout />
            ) : (
              <AuthGuard requiredPlatformAdmin>
                <ShellLayout />
              </AuthGuard>
            )
          }
        >
          <Route path="/infrastructure" element={<InfrastructurePage />} />
          <Route path="/platform-users" element={<PlatformUsersPage />} />
          <Route path="/platform-users/invite" element={<UserAccessWizard />} />
          <Route path="/platform-users/users/:userId" element={<UserDetailPage />} />
          <Route path="/platform-users/users/:userId/edit" element={<UserAccessWizard />} />
          <Route path="/system-roles" element={<SystemRolesPage />} />
        </Route>

        {/* Catch all - redirect to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

function ShellLayout() {
  return (
    <AppShell>
      <Outlet />
    </AppShell>
  );
}
